import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild
} from "@angular/core";
import { IGenomeTime } from "bz-genome-angular/time-picker/time-picker/bzg-time-picker.component";
import { RestApi } from "../../auth/rest.api";
import { TranslateService } from "@ngx-translate/core";
import { BzgModalService, BzgToastService, BzgToastType } from "bz-genome-angular";
import { GenomeTimeService } from "../../shared/services/genome-time.service";
import { ConfirmDialogComponent } from "app/shared/components/confirm-dialog/confirm-dialog.component";
import { ActionConfirmationComponent } from "app/shared/components/action-confirmation/action-confirmation.component";
import { ConfirmationType } from 'app/core/models/confirmation-type.enum';
import { I18nService } from "i18n/i18n.service";
import { DeleteConfirmationComponent } from "app/shared/components/delete-confirmation/delete-confirmation.component";
import { DateUtil } from "app/core/utils/DateUtil";
import { ShowToastService } from 'app/shared/services/showToast.service';
import { DateUtilDST } from "app/core/utils/DateUtilDST";

@Component({
  selector: "maintenance-window",
  templateUrl: "./maintenance-window.component.html",
  styleUrls: ["./maintenance-window.component.scss"]
})
export class MaintenanceWindowComponent implements OnInit {
  messageType: number = 0;
  @Input() status: number = 0;
  @Input() showLoading: boolean = false;
  @Output() maintenanceEmitter = new EventEmitter();

  @ViewChild("dpTo") dpTo: ElementRef;
  @ViewChild("dpFrom") dpFrom: ElementRef;
  @ViewChild("upcomingMessageForm") upcomingMessageForm: ElementRef;
  @ViewChild("customizeMessageForm") customizeMessageForm: ElementRef;

  public upcomingMessage: IUpcomingMessage;
  public customizedMessage: ICustomizedMessage;
  public startedDateTime: IGenomeTime;
  public test: IGenomeTime;
  public finishedDateTime: IGenomeTime;
  public isOpenModal: boolean = false;
  public accordionCollapsedState: boolean[] = [true, true];

  private _showMinMaxDateToast = true;
  private _pickers: any = { dpTo: false, dpFrom: false };
  baseUrlExt = 'api/Maintenance/';

  constructor(
    private _api: RestApi,
    private _i18nService: I18nService,
    private _translateService: TranslateService,
    private _toastService: BzgToastService,
    private _genomeTimeService: GenomeTimeService,
    private _modalService: BzgModalService,
    private _showToastService: ShowToastService
  ) {
    this.upcomingMessage = {
      id: "",
      startedDate: null,
      finishedDate: null,
      message: "",
      modifiedDate: null
    };

    this.customizedMessage = {
      title: "",
      description: ""
    };

    this.startedDateTime = this._getDefaultTime();

    this.finishedDateTime = this._getDefaultTime();
  }

  ngOnInit() {
    this.getShowUpcomingMessage();
    this.getCustomizeMessage();
  }

  accordionHandler(isOpened: Event, index: number) {
    this.accordionCollapsedState = this.accordionCollapsedState.map(() => true);
    this.accordionCollapsedState[index] = Boolean(!isOpened);
  }

  changeStatus() {
    if (this.status === 0 && !this.isOpenModal) {
      this.isOpenModal = true;
      const title = this._i18nService.instant("MAINTENANCE.TITLE_MAINTENANCE");
      const message = this._i18nService.instant("MAINTENANCE.START_MAINTENANCE_MESSAGE");
      const button = this._i18nService.instant("GLOBAL.START");
      const modal = this._modalService.open(ActionConfirmationComponent, {
        closeOnBackdropClick: false,
        closeOnRouterNavigation: true,
        data: { title, message, type: ConfirmationType.CONFIRMATION, button }
      });
      modal.afterClosed().subscribe((result) => {
        this.isOpenModal = false;
        if (result === ConfirmationType.CONFIRMATION) {
          this.messageType = 0;
          this.maintenanceEmitter.emit();
        }
      });
    } else {
      this.messageType = 1;
      this.maintenanceEmitter.emit();
    }
  }

  public isFormValid(form) {
    let valid = true;

    Object.keys(form.form.controls).map(function (c) {
      const control = form.form.controls[c];
      if (control.invalid) {
        valid = false;
        return;
      }
    });
    return valid && !this.areDatesInvalid();
  }

  public areDatesInvalid() {
    let areInvalid = false;

    if (this.upcomingMessage.startedDate && this.upcomingMessage.finishedDate) {
      this._genomeTimeService.setTimeToDate(
        this.upcomingMessage.startedDate,
        this.startedDateTime
      );
      this._genomeTimeService.setTimeToDate(
        this.upcomingMessage.finishedDate,
        this.finishedDateTime
      );
      areInvalid =
        this.upcomingMessage.startedDate >= this.upcomingMessage.finishedDate;

      if (areInvalid) {
        if (this._showMinMaxDateToast) {
          this._showToastService.showToast(this._i18nService.instant("MAINTENANCE.INITIAL_DATE_GREATER_THAN_FINAL"), 'error');
        }
        this._showMinMaxDateToast = false;
      } else {
        this._showMinMaxDateToast = true;
      }
    } else {
      this._showMinMaxDateToast = true;
    }

    return areInvalid;
  }

  public setDatePickerActive(picker) {
    this._pickers.dpFrom = false;
    this._pickers.dpTo = false;
    this._pickers[picker] = true;
  }

  public getShowUpcomingMessage() {
    this._api.get(`${this.baseUrlExt}ShowUpcoming`).subscribe(
      message => {
        if (message) {
          this.upcomingMessage.id = message.id;
          this.upcomingMessage.message = this.unescapeQuotes(message.message);

           this.upcomingMessage.startedDate = DateUtilDST.normalizeUtcToTimeZoneDate(message.startedDate);
          this.upcomingMessage.finishedDate = DateUtilDST.normalizeUtcToTimeZoneDate(message.finishedDate);
          this.upcomingMessage.modifiedDate = DateUtilDST.normalizeUtcToTimeZoneDate(message.modifiedDate);

          this.startedDateTime = DateUtil.getTimeFromDate(this.upcomingMessage.startedDate);
          this.finishedDateTime = DateUtil.getTimeFromDate(this.upcomingMessage.finishedDate);
        }
      },
      err => {
        this._showToastService.showToast(this._i18nService.instant("GLOBAL.GENERIC_ERROR_MSG"), 'error');
      }
    );
  }

  public updateShowUpcomingMessage() {
    if (this.isFormValid(this.upcomingMessageForm)) {
      this._genomeTimeService.setTimeToDate(
        this.upcomingMessage.startedDate,
        this.startedDateTime
      );
      this._genomeTimeService.setTimeToDate(
        this.upcomingMessage.finishedDate,
        this.finishedDateTime
      );
      let upcomingMessage: IUpcomingMessage;
      upcomingMessage = { ...this.upcomingMessage };
      upcomingMessage.message = this.scapeQuotes(upcomingMessage.message);

      upcomingMessage.startedDate = DateUtilDST.convertFromUTC(upcomingMessage.startedDate) as any;
      upcomingMessage.finishedDate = DateUtilDST.convertFromUTC(upcomingMessage.finishedDate) as any;
      if (!upcomingMessage.modifiedDate)
        upcomingMessage.modifiedDate = new Date();
      upcomingMessage.modifiedDate = DateUtilDST.convertFromUTC(upcomingMessage.modifiedDate) as any;
      this._api
        .post(`${this.baseUrlExt}ShowUpcoming`, upcomingMessage)
        .subscribe(
          () => {
            this._showToastService.showToast(this._i18nService.instant("GLOBAL.THE_OPERATION_WAS_SAVED_SUCCESSFULLY"), 'success');
          },
          err => {
            this._showToastService.showToast(this._i18nService.instant("GLOBAL.GENERIC_ERROR_MSG"), 'error');
          }
        );
    }
  }

  public clearUpcomingMessage() {
    this.upcomingMessage = {
      id: "",
      startedDate: null,
      finishedDate: null,
      message: "",
      modifiedDate: null
    };
    this.startedDateTime = this._getDefaultTime();
    this.finishedDateTime = this._getDefaultTime();
  }

  public getCustomizeMessage() {
    this._api.get(`${this.baseUrlExt}CustomizeMessage`).subscribe(
      message => {
        const responseMessage: ICustomizedMessage = message as ICustomizedMessage;
        responseMessage.description = this.unescapeQuotes(
          responseMessage.description
        );
        responseMessage.title = this.unescapeQuotes(responseMessage.title);
        this.customizedMessage = responseMessage;
      },
      err => {
        this._showToastService.showToast(this._i18nService.instant("GLOBAL.GENERIC_ERROR_MSG"), 'error');
      }
    );
  }

  public updateCustomizeMessage() {
    if (this.isFormValid(this.customizeMessageForm)) {
      let message: ICustomizedMessage;
      message = { ...this.customizedMessage };
      message.description = this.scapeQuotes(message.description);
      message.title = this.scapeQuotes(message.title);
      this._api
        .post(`${this.baseUrlExt}CustomizeMessage`, message)
        .subscribe(
          () => {
            this._showToastService.showToast(this._i18nService.instant("GLOBAL.THE_OPERATION_WAS_SAVED_SUCCESSFULLY"), 'success');
          },
          err => {
            this._showToastService.showToast(this._i18nService.instant("GLOBAL.GENERIC_ERROR_MSG"), 'error');
          }
        );
    }
  }

  public clearCustomizeMessage() {
    this.customizedMessage.title = "";
    this.customizedMessage.description = "";
  }

  private _getDefaultTime(): IGenomeTime {
    return {
      hours: 12,
      minutes: 0,
      seconds: 0,
      period: "AM"
    };
  }

  private scapeQuotes(message: string): string {
    if (message) {
      return message.replace(/"/g, '\\"');
    }
    return message;
  }

  private unescapeQuotes(message: string): string {
    if (message) {
      return message.replace(/\\"/g, '"');
    }
    return message;
  }
}
